import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from '@reduxjs/toolkit'
import { StageService } from 'services/crm'
import { fetchPipeline } from './pipelinesSlice'

const stagesAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.order - b.order,
})

export const fetchStagesByPipeline = createAsyncThunk(
  'crm/stages/fetchByPipeline',
  async (pipelineId, { rejectWithValue }) => {
    try {
      const res = await StageService.getByPipeline(pipelineId)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error fetching stages')
    }
  },
)

export const createStage = createAsyncThunk(
  'crm/stages/create',
  async ({ pipelineId, data }, { rejectWithValue }) => {
    try {
      const res = await StageService.create(pipelineId, data)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error creating stage')
    }
  },
)

export const updateStage = createAsyncThunk(
  'crm/stages/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await StageService.update(id, data)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error updating stage')
    }
  },
)

export const removeStage = createAsyncThunk(
  'crm/stages/remove',
  async (id, { rejectWithValue }) => {
    try {
      await StageService.remove(id)
      return id
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error deleting stage')
    }
  },
)

export const reorderStages = createAsyncThunk(
  'crm/stages/reorder',
  async ({ pipelineId, stageIds }, { rejectWithValue }) => {
    try {
      const res = await StageService.reorder(pipelineId, stageIds)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error reordering stages')
    }
  },
)

const initialState = stagesAdapter.getInitialState({
  loading: 'idle',
  error: null,
})

const stagesSlice = createSlice({
  name: 'crmStages',
  initialState,
  reducers: {
    optimisticReorder: (state, action) => {
      const { stageIds } = action.payload
      stageIds.forEach((id, idx) => {
        const stage = state.entities[id]
        if (stage) stage.order = idx
      })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStagesByPipeline.fulfilled, (state, action) => {
        stagesAdapter.upsertMany(state, action.payload)
      })
      .addCase(fetchPipeline.fulfilled, (state, action) => {
        if (Array.isArray(action.payload?.stages)) {
          stagesAdapter.upsertMany(state, action.payload.stages)
        }
      })
      .addCase(createStage.fulfilled, (state, action) => {
        stagesAdapter.upsertOne(state, action.payload)
      })
      .addCase(updateStage.fulfilled, (state, action) => {
        stagesAdapter.upsertOne(state, action.payload)
      })
      .addCase(removeStage.fulfilled, (state, action) => {
        stagesAdapter.removeOne(state, action.payload)
      })
      .addCase(reorderStages.fulfilled, (state, action) => {
        stagesAdapter.upsertMany(state, action.payload)
      })
  },
})

export const { optimisticReorder } = stagesSlice.actions

const selectors = stagesAdapter.getSelectors((state) => state.crmStages)
export const selectAllStages = selectors.selectAll
export const selectStageById = selectors.selectById

export const selectStagesByPipeline = (pipelineId) =>
  createSelector([selectAllStages], (stages) =>
    stages.filter((s) => s.pipelineId === pipelineId).sort((a, b) => a.order - b.order),
  )

export default stagesSlice.reducer
